/**
 * DynamicFormRenderer
 * Renders forms dynamically based on FormTemplate schema
 * Handles data collection and validation for all field types
 * Supports inline editing with add/delete/edit controls
 */

import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import type { FormTemplate, PageSection, FieldSchema } from '../../types/formSchema';
import DynamicFieldRenderer from './DynamicFieldRenderer';
import EditableFieldWrapper from './EditableFieldWrapper';
import FieldConfigModal from './FieldConfigModal';

interface DynamicFormRendererProps {
  template: FormTemplate;
  pageId: string; // Which page to render
  formData: Record<string, any>; // Current form data
  onDataChange: (fieldId: string, value: any) => void;
  onPhotoUpload?: (fieldId: string, files: File[]) => Promise<void>;
  readOnly?: boolean;
  editMode?: boolean; // Enable inline editing controls
  onTemplateChange?: (template: FormTemplate) => void; // For saving template changes
}

export default function DynamicFormRenderer({
  template,
  pageId,
  formData,
  onDataChange,
  onPhotoUpload,
  readOnly = false,
  editMode = false,
  onTemplateChange,
}: DynamicFormRendererProps) {
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [editingField, setEditingField] = useState<FieldSchema | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Find the current page
  const currentPage = template.pages.find((p) => p.id === pageId);

  if (!currentPage) {
    return (
      <div className="p-8 text-center text-gray-500">
        <p>Page not found: {pageId}</p>
      </div>
    );
  }

  // Sort fields by order
  const sortedFields = [...currentPage.fields].sort((a, b) => a.order - b.order);

  // Group fields by section
  const fieldsBySection: Record<string, FieldSchema[]> = {};
  const fieldsWithoutSection: FieldSchema[] = [];
  
  sortedFields.forEach((field) => {
    if (field.section) {
      if (!fieldsBySection[field.section]) {
        fieldsBySection[field.section] = [];
      }
      fieldsBySection[field.section].push(field);
    } else {
      fieldsWithoutSection.push(field);
    }
  });
  
  // Get section names in order
  const sectionNames = Object.keys(fieldsBySection);

  // Validate a single field
  const validateField = (field: FieldSchema, value: any): string | null => {
    // Required field validation
    if (field.required) {
      if (value === undefined || value === null || value === '') {
        return `${field.label} is required`;
      }

      // Array fields (multi-choice, images)
      if (Array.isArray(value) && value.length === 0) {
        return `${field.label} is required`;
      }
    }

    // Type-specific validation
    if (field.type === 'number' && value !== undefined && value !== null && value !== '') {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return `${field.label} must be a number`;
      }

      if (field.min !== undefined && numValue < field.min) {
        return `${field.label} must be at least ${field.min}`;
      }

      if (field.max !== undefined && numValue > field.max) {
        return `${field.label} must be at most ${field.max}`;
      }
    }

    if (field.type === 'text' && value) {
      if (field.maxLength && value.length > field.maxLength) {
        return `${field.label} must be ${field.maxLength} characters or less`;
      }

      if (field.pattern) {
        const regex = new RegExp(field.pattern);
        if (!regex.test(value)) {
          return field.helpText || `${field.label} format is invalid`;
        }
      }
    }

    return null;
  };

  // Handle field change with validation
  const handleFieldChange = (fieldId: string, value: any) => {
    // Update data
    onDataChange(fieldId, value);

    // Validate
    const field = sortedFields.find((f) => f.id === fieldId);
    if (field) {
      const error = validateField(field, value);
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[fieldId] = error;
        } else {
          delete newErrors[fieldId];
        }
        return newErrors;
      });
    }
  };

  // Edit mode handlers
  const handleEditField = (field: FieldSchema) => {
    setEditingField(field);
    setIsModalOpen(true);
  };

  const handleSaveField = (updatedField: FieldSchema) => {
    if (!onTemplateChange) return;

    const updatedPages = template.pages.map((page) => {
      if (page.id === pageId) {
        return {
          ...page,
          fields: page.fields.map((f) =>
            f.id === updatedField.id ? updatedField : f
          ),
        };
      }
      return page;
    });

    onTemplateChange({ ...template, pages: updatedPages });
  };

  const handleDeleteField = (fieldId: string) => {
    if (!onTemplateChange) return;

    const updatedPages = template.pages.map((page) => {
      if (page.id === pageId) {
        return {
          ...page,
          fields: page.fields.filter((f) => f.id !== fieldId),
        };
      }
      return page;
    });

    onTemplateChange({ ...template, pages: updatedPages });
  };

  const handleMoveField = (fieldId: string, direction: 'up' | 'down') => {
    if (!onTemplateChange) return;

    const updatedPages = template.pages.map((page) => {
      if (page.id === pageId) {
        const fields = [...page.fields].sort((a, b) => a.order - b.order);
        const index = fields.findIndex((f) => f.id === fieldId);
        
        if (
          (direction === 'up' && index > 0) ||
          (direction === 'down' && index < fields.length - 1)
        ) {
          const newIndex = direction === 'up' ? index - 1 : index + 1;
          [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];
          
          // Update order values
          fields.forEach((field, idx) => {
            field.order = idx;
          });
        }

        return { ...page, fields };
      }
      return page;
    });

    onTemplateChange({ ...template, pages: updatedPages });
  };

  const handleAddField = (afterFieldId?: string) => {
    if (!onTemplateChange) return;

    // Create new field
    const newOrder = afterFieldId
      ? (sortedFields.find((f) => f.id === afterFieldId)?.order || 0) + 1
      : sortedFields.length;

    const newField: FieldSchema = {
      id: `field_${Date.now()}`,
      type: 'text',
      label: 'New Field',
      placeholder: '',
      required: false,
      order: newOrder,
    };

    // Adjust orders of fields after insertion point
    const updatedPages = template.pages.map((page) => {
      if (page.id === pageId) {
        const updatedFields = page.fields.map((f) => {
          if (f.order >= newOrder) {
            return { ...f, order: f.order + 1 };
          }
          return f;
        });

        return {
          ...page,
          fields: [...updatedFields, newField],
        };
      }
      return page;
    });

    onTemplateChange({ ...template, pages: updatedPages });

    // Open modal to configure new field
    setEditingField(newField);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{currentPage.title}</h2>
        {currentPage.description && (
          <p className="mt-2 text-sm text-gray-600">{currentPage.description}</p>
        )}
      </div>

      {/* Add Field Button (top) */}
      {editMode && (
        <button
          onClick={() => handleAddField()}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          <span className="font-medium">Add Field Here</span>
        </button>
      )}

      {/* Fields */}
      <div className="space-y-6">
        {/* Fields without sections */}
        {fieldsWithoutSection.length > 0 && (
          <div className="space-y-4">
            {fieldsWithoutSection.map((field, index) => {
              const fieldRenderer = (
                <DynamicFieldRenderer
                  key={field.id}
                  field={field}
                  value={formData[field.id]}
                  onChange={(value: any) => handleFieldChange(field.id, value)}
                  formData={formData}
                  onFormDataChange={onDataChange}
                  onPhotoUpload={onPhotoUpload ? (files: File[]) => onPhotoUpload(field.id, files) : undefined}
                  error={validationErrors[field.id]}
                  readOnly={readOnly}
                />
              );

              return (
                <React.Fragment key={field.id}>
                  {editMode ? (
                    <EditableFieldWrapper
                      field={field}
                      onEdit={handleEditField}
                      onDelete={handleDeleteField}
                      onMove={handleMoveField}
                      isFirst={index === 0}
                      isLast={index === sortedFields.length - 1}
                    >
                      {fieldRenderer}
                    </EditableFieldWrapper>
                  ) : (
                    fieldRenderer
                  )}

                  {editMode && (
                    <button
                      onClick={() => handleAddField(field.id)}
                      className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Field</span>
                    </button>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Sectioned fields */}
        {sectionNames.map((sectionName) => (
          <fieldset key={sectionName} className="border border-gray-300 p-4 rounded-lg space-y-4">
            <legend className="text-md font-semibold text-gray-700 px-2">{sectionName}</legend>
            {fieldsBySection[sectionName].map((field, index) => {
              const fieldRenderer = (
                <DynamicFieldRenderer
                  key={field.id}
                  field={field}
                  value={formData[field.id]}
                  onChange={(value: any) => handleFieldChange(field.id, value)}
                  formData={formData}
                  onFormDataChange={onDataChange}
                  onPhotoUpload={onPhotoUpload ? (files: File[]) => onPhotoUpload(field.id, files) : undefined}
                  error={validationErrors[field.id]}
                  readOnly={readOnly}
                />
              );

              return (
                <React.Fragment key={field.id}>
                  {editMode ? (
                    <EditableFieldWrapper
                      field={field}
                      onEdit={handleEditField}
                      onDelete={handleDeleteField}
                      onMove={handleMoveField}
                      isFirst={index === 0}
                      isLast={index === fieldsBySection[sectionName].length - 1}
                    >
                      {fieldRenderer}
                    </EditableFieldWrapper>
                  ) : (
                    fieldRenderer
                  )}

                  {editMode && (
                    <button
                      onClick={() => handleAddField(field.id)}
                      className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Field</span>
                    </button>
                  )}
                </React.Fragment>
              );
            })}
          </fieldset>
        ))}
      </div>

      {/* Validation Summary */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-sm font-semibold text-red-800 mb-2">
            Please fix the following errors:
          </h3>
          <ul className="list-disc list-inside space-y-1">
            {Object.entries(validationErrors).map(([fieldId, error]) => (
              <li key={fieldId} className="text-sm text-red-700">
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Field Configuration Modal */}
      {editMode && (
        <FieldConfigModal
          field={editingField}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingField(null);
          }}
          onSave={handleSaveField}
          onDelete={
            editingField ? () => handleDeleteField(editingField.id) : undefined
          }
        />
      )}
    </div>
  );
}
